import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { serversApi } from '../api'
import { useAdminServerEvents } from './useSignalR'
import type { EdgeServer, ServerCommandType } from '../types'

export type CommandToast = { message: string; type: 'success' | 'error' }

// 원격 모니터링 시작/중지 — 명령 전송(202) 후 SignalR ServerCommandCompleted 로 결과 수신.
// ServerCommandIssued 브로드캐스트도 구독해 다른 브라우저에서 발행된 명령의 "처리 중" 인디케이터도
// 함께 표시한다(중복 클릭 방지). 60초 무응답 시 타임아웃 처리. Admin 화면 공용.
export function useMonitoringCommand(enabled: boolean) {
  const qc = useQueryClient()
  const [toast, setToast] = useState<CommandToast | null>(null)
  // serverId → 진행 중인 명령 타입 (응답 대기 중)
  const [pendingCommands, setPendingCommands] = useState<Record<string, ServerCommandType>>({})
  const timersRef = useRef<Record<string, number>>({})

  const setPending = (serverId: string, type: ServerCommandType) => {
    setPendingCommands(p => ({ ...p, [serverId]: type }))
    if (timersRef.current[serverId]) clearTimeout(timersRef.current[serverId])
    timersRef.current[serverId] = window.setTimeout(() => {
      clearPending(serverId)
      setToast({ message: '명령 응답이 없습니다. Edge 상태를 확인하세요.', type: 'error' })
    }, 60_000)
  }

  const clearPending = (serverId: string) => {
    const timer = timersRef.current[serverId]
    if (timer) {
      clearTimeout(timer)
      delete timersRef.current[serverId]
    }
    setPendingCommands(p => {
      const { [serverId]: _, ...rest } = p
      return rest
    })
  }

  const mutation = useMutation({
    mutationFn: ({ serverId, type }: { serverId: string; type: ServerCommandType }) =>
      serversApi.sendCommand(serverId, type),
    onSuccess: (_res, { serverId, type }) => setPending(serverId, type),
    onError: (err: { response?: { data?: { error?: string } } }) => {
      setToast({ message: err.response?.data?.error ?? '명령 전송에 실패했습니다.', type: 'error' })
    },
  })

  useAdminServerEvents(
    enabled,
    (serverId, _commandId, success, message) => {
      clearPending(serverId)
      setToast(success
        ? { message: '명령이 완료되었습니다.', type: 'success' }
        : { message: message || '명령이 실패했습니다.', type: 'error' })
      qc.invalidateQueries({ queryKey: ['servers'] })
    },
    () => qc.invalidateQueries({ queryKey: ['servers'] }),
    // 다른 관리자 브라우저가 발행한 명령도 인디케이터 반영
    (serverId, _commandId, type) => setPending(serverId, type as ServerCommandType),
  )

  // 언마운트 시 타임아웃 타이머 정리
  useEffect(() => () => {
    Object.values(timersRef.current).forEach(clearTimeout)
  }, [])

  const sendCommand = (s: EdgeServer, type: ServerCommandType) => {
    if (type === 'StopMonitoring' && !confirm(`'${s.name}'의 모니터링을 중지하시겠습니까?`))
      return
    mutation.mutate({ serverId: s.id, type })
  }

  return { pendingCommands, sendCommand, isSending: mutation.isPending, toast, setToast }
}
